/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:13:27 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useSearchParams } from "react-router-dom";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AIFormReviewAgentList } from "ai-workers/components/AIFormReviewAgents";
import { FormConfiguration } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { FieldsListEditor } from "form-builder/components";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { useFormsAPI } from "form-builder/services";
import { ProgressWrapper, EmphasisText } from "ui-widgets";
import { FormBuilderTitle } from "./FormBuilderTitle";
import { FormBuilderTopBar } from "./FormBuilderTopBar";
import { AdvancedFormSettings } from "./AdvancedFormSettings";
import { FieldDependencies } from "./FieldDependencies";
import { SubmissionIdentifierSequence } from "./SubmissionIdentifierSequence";

type FormBuilderProps = {
  formId: PrimaryKeyValue<FormConfiguration>;
} & Pick<React.ComponentProps<typeof FormBuilderTopBar>, "onUpdateFormInfo" | "shareSubmissionLinkComponent">;

type IFormBuilderScreen = "fields" | "numbering-sequence" | "field-dependencies" | "advanced" | "ai-form-review-agents";

export const FormBuilder = (props: FormBuilderProps) => {
  const { formId, shareSubmissionLinkComponent, onUpdateFormInfo } = props;

  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);
  const setCurrentForm = useFormBuilderStore((state) => state.setCurrentForm);

  const { useAPIFetchForm } = useFormsAPI();
  const { apiItem: fetchedForm, isMakingAPIRequest } = useAPIFetchForm(formId, { makeRequestOnInitialMount: true });

  React.useEffect(() => {
    if (fetchedForm?.id) {
      setCurrentForm(fetchedForm); // resetProviderFormConfiguration - Resetting the form builder store form config when selected form is changed
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedForm?.id]);

  const [searchParams, setSearchParams] = useSearchParams({ screen: "fields" });
  const [screen, toggleScreen] = [
    searchParams.get("screen") as IFormBuilderScreen,
    (screen: IFormBuilderScreen) => setSearchParams({ screen }),
  ];

  let subTitle: string;
  switch (screen) {
    case "fields":
      subTitle = "Form Fields";
      break;
    case "advanced":
      subTitle = "Advanced Settings";
      break;
    case "field-dependencies":
      subTitle = "Field Dependencies";
      break;
    case "ai-form-review-agents":
      subTitle = "AI Form Review Agents";
      break;
  }

  return (
    <ProgressWrapper showLoader={isMakingAPIRequest} loaderType="linear">
      {currentFormConfiguration?.id && (
        <>
          <FormBuilderTopBar
            onSelectFormFields={() => toggleScreen("fields")}
            onSelectFormFieldDependencies={() => toggleScreen("field-dependencies")}
            onSelectNumberingSequence={() => toggleScreen("numbering-sequence")}
            onSelectAdvancedSettings={() => toggleScreen("advanced")}
            onSelectAIAgents={() => toggleScreen("ai-form-review-agents")}
            onUpdateFormInfo={onUpdateFormInfo}
            shareSubmissionLinkComponent={shareSubmissionLinkComponent}
          />
          <Stack direction="column" spacing={5} className="os-form-builder-container">
            <span className="title">
              <FormBuilderTitle />
            </span>

            <Typography variant="h5" className="subtitle">
              <EmphasisText>{subTitle}</EmphasisText>
            </Typography>
            <div className="content">
              {screen === "fields" && (
                <>
                  <div className="fields-container">
                    <FieldsListEditor />
                  </div>
                </>
              )}
              {screen === "advanced" && <AdvancedFormSettings />}
              {screen === "field-dependencies" && <FieldDependencies />}
              {screen === "numbering-sequence" && <SubmissionIdentifierSequence />}
              {screen === "ai-form-review-agents" && <AIFormReviewAgentList form={currentFormConfiguration} />}
            </div>
          </Stack>
        </>
      )}
    </ProgressWrapper>
  );
};
